import React, { useMemo, useState, useEffect } from "react";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(ArcElement, Tooltip, Legend);

// Purple scheme (Zakupy)
const COLORS = {
  TP: "#9848c7",
  OTHER: "#cdb0ea",
};

const months = [
  { value: 0, label: "Cały rok" },
  { value: 1, label: "Styczeń" },
  { value: 2, label: "Luty" },
  { value: 3, label: "Marzec" },
  { value: 4, label: "Kwiecień" },
  { value: 5, label: "Maj" },
  { value: 6, label: "Czerwiec" },
  { value: 7, label: "Lipiec" },
  { value: 8, label: "Sierpień" },
  { value: 9, label: "Wrzesień" },
  { value: 10, label: "Październik" },
  { value: 11, label: "Listopad" },
  { value: 12, label: "Grudzień" },
];

const parseValue = (value) => {
  const num = parseFloat(value);
  return isNaN(num) || num < 0 ? 0 : num;
};

const formatNumber = (value) =>
  typeof value === "number"
    ? value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")
    : value;

// === Styled Components ===
const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

/**
 * Props:
 * - data: array of purchase rows (zakupy)
 * - metric: "count" | "value" (default "count")
 *    "count" => udział liczby transakcji TP vs inne
 *    "value" => udział wartości (K_42 + K_43) TP vs inne
 */
const TpTransactionsPie = ({ data = [], metric = "count" }) => {
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(0);

  useEffect(() => {
    const years = [...new Set(data.map((row) => row.Rok?.toString()))]
      .filter(Boolean)
      .sort();
    setAvailableYears(years);
    if (!selectedYear && years.length > 0) {
      setSelectedYear(years.includes("2024") ? "2024" : years[0]);
    }
  }, [data, selectedYear]);

  const { tpValue, otherValue, totalCount } = useMemo(() => {
    if (!selectedYear) return { tpValue: 0, otherValue: 0, totalCount: 0 };

    const filtered = data.filter(
      (row) =>
        row.Rok?.toString() === selectedYear &&
        (selectedMonth === 0 || parseInt(row.Miesiac) === selectedMonth)
    );

    let tp = 0;
    let other = 0;
    let count = 0;

    for (const row of filtered) {
      const isTP = row.TP === "1";
      if (metric === "value") {
        const total = parseValue(row.K_42) + parseValue(row.K_43);
        if (isTP) tp += total;
        else other += total;
      } else {
        // metric === "count"
        if (isTP) tp += 1;
        else other += 1;
      }
      count += 1;
    }

    return { tpValue: tp, otherValue: other, totalCount: count };
  }, [data, selectedYear, selectedMonth, metric]);

  const chartData = {
    labels: ["TP (powiązane)", "Inne"],
    datasets: [
      {
        data: [tpValue, otherValue],
        backgroundColor: [COLORS.TP, COLORS.OTHER],
        borderWidth: 0,
      },
    ],
  };

  const total = tpValue + otherValue || 1; // avoid divide by zero
  const tpPct = ((tpValue / total) * 100).toFixed(1);
  const otherPct = ((otherValue / total) * 100).toFixed(1);

  if (!selectedYear || availableYears.length === 0) {
    return <div style={{ textAlign: "center" }}>Ładowanie danych...</div>;
    }

  return (
    <div style={{ width: "100%", margin: "20px auto", textAlign: "center" }}>
      <ChartTitle>
        Transakcje ze spółkami powiązanymi (TP) — {metric === "value" ? "udział wartości" : "udział liczby"}
        <InfoIconWrapper data-tooltip-id="tp-pie-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>

      <InfoTooltip
        id="tp-pie-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Transakcje powiązane (TP)</strong><br />
            Udział {metric === "value" ? "wartości (K_42 + K_43)" : "liczby transakcji"}<br />
            TP (row.TP === "1") vs inne.
          </>
        }
      />

      <div
        style={{
          marginBottom: "16px",
          display: "flex",
          justifyContent: "center",
          gap: "16px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <label style={{ fontWeight: 500 }}>Rok:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            style={{
              padding: "8px 12px",
              fontSize: "15px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <label style={{ fontWeight: 500 }}>Miesiąc:</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            style={{
              padding: "8px 12px",
              fontSize: "15px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ width: "100%", maxWidth: "560px", margin: "0 auto" }}>
        <Pie
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
              legend: { position: "bottom" },
              tooltip: {
                backgroundColor: "#333",
                titleColor: "#fff",
                bodyColor: "#fff",
                callbacks: {
                  label: (context) => {
                    const label = context.label || "";
                    const val = context.raw ?? 0;
                    if (metric === "value") {
                      return `${label}: ${formatNumber(val)} (${((val / total) * 100).toFixed(1)}%)`;
                    }
                    return `${label}: ${val} (${((val / total) * 100).toFixed(1)}%)`;
                  },
                },
              },
            },
          }}
        />
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: "#666" }}>
        {metric === "value"
          ? <>TP: <strong>{tpPct}%</strong> &nbsp;|&nbsp; Inne: <strong>{otherPct}%</strong></>
          : <>TP: <strong>{tpPct}%</strong> &nbsp;|&nbsp; Inne: <strong>{otherPct}%</strong> (z {totalCount} transakcji)</>}
      </div>
    </div>
  );
};

export default TpTransactionsPie;
